// Co-op VISITS on the client (server: server/stays.js; plan: docs/COOP-INVITES-PLAN.md).
//
//   As a HOST (someone is visiting your kingdom): everything you earn is paid into the shared coffer instead of your treasury (a tap on the
//     earning code, seasonManager.setEarningTap); the game sends it to the server every few seconds; if the stay ends first, whatever had not
//     been sent is returned to your treasury. You can ask for a count (Count now) or send a guest away (Oust).
//   As a GUEST: accepting an invitation travels you to the host's kingdom (an adventure-style trip: your own home keeps running and can be raided).
//     You press Work to fill the coffer (the SERVER decides how much), and go home when you like — or when the host sends you away / time is up.
//   Either way: the coin the server credits you (your wage, the host's payout) is added to your treasury exactly once (profile.coopApplied,
//     the same safe pattern as bought coins: the server keeps a running total, the game applies only the difference).
//
//   While you host or visit the game asks the server every few seconds instead of every 20 s (inviteManager POLL.ENGAGED).

import { Events, on, emit } from '../ui/eventBus.js'
import * as season from './seasonManager.js'
import { getProfile, persist } from './state.js'
import { stayApi } from './accountManager.js'
import { getInvites, refresh, setPollMode, getSkew } from './inviteManager.js'
import { isAwayBusy } from './awayState.js'
import * as advManager from './adventureManager.js'
import { formatCoins } from '../ui/format.js'

const FLUSH_MS = 3000
let tapOn = false
let flushTimer = null
let flushing = false
let inEstate = false
let workBusy = false
const status = new Map() // stay id → status last seen (to notice that a stay ended)
let primed = false

const notice = (text, kind = 'ok', feed = false) => emit(Events.CoopNotice, { text, kind, feed })
export const activeHostStays = () => getInvites().stays.filter((s) => s.role === 'host' && s.status === 'active')
export const activeGuestStay = () => getInvites().stays.find((s) => s.role === 'guest' && s.status === 'active') ?? null
export const isInEstate = () => inEstate
export const getCoop = () => ({ host: activeHostStays(), guest: activeGuestStay(), inEstate, ended: getInvites().stays.filter((s) => s.status !== 'active'), court: getInvites().court, skew: getSkew() })
export const runningStays = () => getInvites().stays.filter((s) => s.status === 'active' && s.pursuit?.status === 'running')

// ── the host's earnings go into the coffer ──────────────────────────────────────────────────────────────
function tap(amount) {
  const p = getProfile()
  p.coopPending = (Number(p.coopPending) || 0) + amount
  persist()
  return amount // all of it
}
function returnPending(why) {
  const p = getProfile()
  const amt = Math.floor(Number(p.coopPending) || 0)
  p.coopPending = 0
  if (amt >= 1) { season.earnWealth(amt); notice(`${formatCoins(amt)} of your earnings had not reached the coffer — ${why} — and went back into your treasury.`, 'ok', true) }
}
async function flushDeposit() {
  const stays = activeHostStays()
  const p = getProfile()
  if (flushing || !stays.length || (Number(p.coopPending) || 0) < 1) return
  flushing = true
  try {
    const share = Math.floor((Number(p.coopPending) || 0) / stays.length)
    for (const s of stays) {
      if (share < 1) break
      try {
        const r = await stayApi.deposit(s.id, share)
        getProfile().coopPending = Math.max(0, (Number(getProfile().coopPending) || 0) - share)
        if (r.returned > 0) { season.earnWealth(r.returned); notice(`The coffer takes at most ${formatCoins(r.depositCap)} from you a day — ${formatCoins(r.returned)} stayed in your treasury.`, 'ok') }
        s.coffer = r.coffer; s.depositToday = r.depositToday
      } catch (e) { if (e?.body?.ended) { await refresh(); break } /* offline: keep it and try again */ }
    }
    persist(); emitChange()
  } finally { flushing = false }
}

// ── credits: what the server has counted for me ──────────────────────────────────────────────────────────────
function settleCredits() {
  const p = getProfile()
  if (!p?.onboarded) return
  const total = Math.floor(Number(getInvites().credits?.total) || 0)
  const applied = Math.floor(Number(p.coopApplied) || 0)
  if (total === applied) return
  p.coopApplied = total // set first: earnWealth saves, so the marker and the coin are saved together
  if (total > applied) { const d = total - applied; season.earnWealth(d); notice(`${formatCoins(d)} from the coffer ledger was added to your treasury.`, 'ok', true) }
  else { // a fine from the court: taken from what you EARNED — coin you bought with real money is never taken
    const want = applied - total
    const room = Math.max(0, Math.floor(season.getWealth() - (Number(p.coinsApplied) || 0)))
    const taken = Math.min(want, room)
    if (taken > 0) season.fineWealth(taken)
    notice(taken >= want ? `The court took ${formatCoins(taken)} from your treasury.` : `The court took ${formatCoins(taken)} from your treasury — the rest could not be taken (coin you bought is never taken; what is still owed is repaid from your next wages).`, 'warn', true)
  }
}

// ── the guest's visit ─────────────────────────────────────────────────────────────────────────────────────────
export function canTravelNow() {
  if (isAwayBusy() || advManager.isAdventuring?.()) return { ok: false, reason: 'Come home from your trip first — you cannot travel to another kingdom while you are away.' }
  return { ok: true }
}
export function beginVisit(stay = activeGuestStay()) {
  if (!stay) return false
  if (!canTravelNow().ok) { notice('Come home first, then press "Go there".', 'warn'); return false }
  emit(Events.VisitLand, { id: 'guest_estate', name: `${stay.host.name}'s kingdom`, stayId: stay.id })
  return true
}
on(Events.CoopVisitStart, ({ stay }) => beginVisit(stay))
on(Events.LandChange, ({ id, active }) => {
  const now = id === 'guest_estate' && !!active
  if (inEstate && !now) { // the guest pressed RETURN HOME (or H): tell the server — the final count is made now
    const s = activeGuestStay()
    if (s) stayApi.leave(s.id).catch(() => {}).finally(() => refresh())
  }
  inEstate = now
  emitChange()
})

export async function work() {
  const s = activeGuestStay()
  if (!s || workBusy) return null
  workBusy = true
  try {
    const r = await stayApi.work(s.id)
    s.coffer = r.coffer; s.workToday = r.workToday
    emitChange()
    return r
  } catch (e) {
    if (e?.body?.capped) notice('That is all the work you can do for one day.', 'warn')
    else if (e?.body?.ended) await refresh()
    return null
  } finally { workBusy = false }
}
export async function leaveStay() { // from the banner, when the guest is not in the estate
  const s = activeGuestStay(); if (!s) return
  try { await stayApi.leave(s.id) } catch { /* already over */ }
  await refresh()
}

// Run off with the coffer (the guest's choice). The host has the heist window to catch you.
export async function heist() {
  const s = activeGuestStay()
  if (!s) return null
  try { const r = await stayApi.heist(s.id); await refresh(); return r } catch (e) { notice(e?.body?.error ?? 'Could not do that right now.', 'warn'); await refresh(); return null }
}

// ── the host's controls ───────────────────────────────────────────────────────────────────────────────────────
export async function countNow(stayId) {
  try {
    const r = await stayApi.count(stayId)
    const c = r.counted
    notice(c.paid > 0 ? `Counted ${formatCoins(c.paid)}: ${formatCoins(c.host)} to you, ${formatCoins(c.wage)} wage to your guest.` : 'The coffer is empty — nothing to count yet.', 'ok')
  } catch (e) { notice(e?.body?.error ?? 'Could not count right now.', 'warn') }
  await refresh()
}
export async function seize(stayId) {
  try { await stayApi.seize(stayId) } catch (e) { notice(e?.body?.error ?? 'Could not catch them.', 'warn') }
  await refresh()
}
export async function oust(stayId) {
  try { await stayApi.oust(stayId); notice('Your guest was sent home. The coffer was counted.', 'ok') } catch (e) { notice(e?.body?.error ?? 'Could not send them away.', 'warn') }
  await refresh()
}


// ── what to tell the player when a stay ends ───────────────────────────────────────────────────────────────────
const ANN_KEY = 'mao-kingdom/coop/announced/v1'
const readAnnounced = () => { try { return new Set(JSON.parse(localStorage.getItem(ANN_KEY) || '[]')) } catch { return new Set() } }
const writeAnnounced = (set) => { try { localStorage.setItem(ANN_KEY, JSON.stringify([...set].slice(-60))) } catch { /* private mode: fine */ } }
function outcomeNotice(s) {
  const them = s.role === 'host' ? s.guest.name : s.host.name
  const pu = s.pursuit
  if (pu?.status === 'caught') {
    if (s.role === 'host') return { kind: 'ok', text: `You caught ${them}! The whole coffer (${formatCoins(pu.coffer)}) is yours again${pu.bonus ? `, and the court added ${formatCoins(pu.bonus)}` : ''}.` }
    const until = getInvites().court?.bannedUntil
    return { kind: 'warn', text: `Caught! ${them} seized the coffer. The court fined you ${formatCoins(pu.fine)}${pu.debt ? ` and you still owe ${formatCoins(pu.debt)} (repaid from your next wages)` : ''}${until ? `, and you may not visit anyone until ${new Date(until).toLocaleDateString()}` : ''}.` }
  }
  if (pu?.status === 'escaped') return s.role === 'host' ? { kind: 'warn', text: `${them} got away with ${formatCoins(pu.coffer)} from the coffer.` } : { kind: 'ok', text: `You got away with ${formatCoins(pu.coffer)}!` }
  if (s.role === 'host') return { kind: 'ok', text: s.status === 'left' ? `${them} went home.` : s.status === 'ousted' ? `You sent ${them} away.` : `${them}'s stay is over (time was up).` }
  return { kind: s.status === 'ousted' ? 'warn' : 'ok', text: s.status === 'left' ? 'You went home.' : s.status === 'ousted' ? `${them} sent you away.` : `Your stay in ${them}'s kingdom is over (time was up).` }
}

// ── keeping everything in step with the server ─────────────────────────────────────────────────────────────────
function emitChange() { emit(Events.CoopChange, getCoop()) }

function sync() {
  const st = getInvites()
  if (!st.loaded) return
  const host = activeHostStays(), guest = activeGuestStay()
  if (host.length && !tapOn) { season.setEarningTap(tap); tapOn = true }
  if (!host.length && tapOn) { season.setEarningTap(null); tapOn = false }
  if (!host.length) returnPending('the visit is over')
  if (host.length && !flushTimer) flushTimer = setInterval(flushDeposit, FLUSH_MS)
  if (!host.length && flushTimer) { clearInterval(flushTimer); flushTimer = null }
  setPollMode(runningStays().length ? 'PURSUIT' : host.length || guest ? 'ENGAGED' : 'SLOW')
  const court = st.court
  if (court && getProfile()?.onboarded && getProfile().coopCaught !== court.caught) { getProfile().coopCaught = court.caught; persist() } // (the court's record: it costs Honor)
  settleCredits()
  // a theft that started since last time: the host must hear about it NOW (the panel shows the alert; this is the scribe's line)
  for (const s of st.stays) {
    const key = s.id + '#heist'
    if (s.pursuit?.status === 'running' && !status.has(key)) {
      status.set(key, 'running')
      if (primed && s.role === 'host') notice(`${s.guest.name} is running off with the coffer — ${formatCoins(s.pursuit.coffer)}! SEIZE them before time runs out.`, 'alert', true)
    }
  }
  // a stay that ended since last time: tell the player. (A stay that ended while the game was closed is told once, at the next sign-in: a theft the host
  // missed, or a catch the guest was not there to see.)
  const ann = readAnnounced()
  for (const s of st.stays) {
    const was = status.get(s.id)
    status.set(s.id, s.status)
    if (s.status === 'active') continue
    const live = primed && was === 'active'
    const missed = !primed && !ann.has(s.id) && Date.now() - (s.endedAt ?? 0) < 72 * 3600_000 && ((s.role === 'host' && s.pursuit?.status === 'escaped') || (s.role === 'guest' && ['caught', 'escaped'].includes(s.pursuit?.status)))
    if (!live && !missed) continue
    const { text, kind } = outcomeNotice(s)
    notice(missed ? `While you were away: ${text}` : text, kind, true)
    ann.add(s.id)
    if (live && s.role === 'guest' && inEstate) emit(Events.LeaveLand, {}) // the visit is over: go home
  }
  writeAnnounced(ann)
  primed = true
  emitChange()
}
on(Events.InvitesChange, () => sync())
on(Events.AccountReady, ({ loggedIn }) => { if (!loggedIn) { season.setEarningTap(null); tapOn = false; clearInterval(flushTimer); flushTimer = null; status.clear(); primed = false } })
if (typeof window !== 'undefined' && import.meta.env?.DEV) window.__coop = { get: getCoop, flush: flushDeposit, beginVisit, work, countNow, oust, leaveStay, canTravelNow, heist, seize }
